import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { FieldItem } from '../../types';
import * as actions from './action-types';
import { domainNodeToComplexType, mapSelectionType, setDefaultValue } from './actions.mapper';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { selectionReportTranslations } from '../../constants';
import { SelectionFieldItem } from './types-fields';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { selectGroupByFieldState } from 'app/area/reports/selectors';
import { selectAggregationForSelectedReport, selectExposure, selectWorkingDefinition } from 'app/area/favourite/selectors';
import { actionCreators as favouriteActionCreatorsFactory } from 'app/area/favourite/actions';
import { selectSelectionFieldItemsForCurrentReport } from 'app/area/reports/containers/group-by/selectors';
import { getCaseTypeIds } from 'app/area/reports/caseTypeIds';
import { mapToSelectionDefinition } from 'app/area/reports/containers/group-by/map-to-definition';

// eslint:disable-next-line: max-func-body-length
export const actionCreators = (api: ReportCommonApi, instanceKey: string) => {

  const {
    setWorkingDefinitionSelections
  } = favouriteActionCreatorsFactory(instanceKey);

  return {
    clearState: (): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(new actions.ClearGroupByState(instanceKey));
      },
    loadGroupByDefaultFields: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        dispatch(new actions.RequestGroupByDefaultFields(instanceKey));
        const appState = getState();
        const workingDefinition = selectWorkingDefinition(instanceKey)(appState);
        const { aggregationDomain, aggregationType } = selectAggregationForSelectedReport(instanceKey)(appState);
        const { exposureApplication } = selectExposure(instanceKey)(appState);
        const codetables = await api.loadGroupByDefaultFields(
          exposureApplication?.v ? exposureApplication?.v : undefined, 
          aggregationDomain?.v ? aggregationDomain?.v : undefined, 
          aggregationType?.v ? aggregationType?.v : undefined, 
          getCaseTypeIds(workingDefinition)
        );
        const options = codetables?.data?.map(domainNodeToComplexType);
        const selectedGroupByFields = selectSelectionFieldItemsForCurrentReport(instanceKey)(getState());
        dispatch(new actions.ResponseGroupByDefaultFields(instanceKey, [
          ...(options || []),
          ...(selectedGroupByFields
            ?.filter(g => !options.some(o => o.value === g.field.value))
            ?.map(g => ({
              ...g.field
            })) || [])
        ]));
      }),
    updateDefaultGroupByFields: (fieldItems: FieldItem[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        dispatch(new actions.UpdateGroupByDefaultFields(instanceKey, fieldItems));
      }),
    loadGroupBySortOrderType: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        dispatch(new actions.RequestGroupBySortOrderType(instanceKey));
        const codetables = await api.loadGroupBySortOrderType();
        const options = codetables.data.rootCodes.map(codeItemToStoreValue);
        dispatch(new actions.ResponseGroupBySortOrderType(instanceKey, options));
      }),
    loadGroupByFields: (query?: string): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        const appState = getState();
        const workingDefinition = selectWorkingDefinition(instanceKey)(appState);
        const { aggregationDomain, aggregationType } = selectAggregationForSelectedReport(instanceKey)(appState);
        const { exposureApplication } = selectExposure(instanceKey)(appState);
        const codetables = await api.loadGroupByFields(
          query, 
          exposureApplication?.v ? exposureApplication?.v : undefined, 
          aggregationDomain?.v ? aggregationDomain?.v : undefined, 
          aggregationType?.v ? aggregationType?.v : undefined,
          getCaseTypeIds(workingDefinition)
        );
        const options = codetables.data.map(domainNodeToComplexType);
        dispatch(new actions.ResponseGroupByFields(instanceKey, options));
      }),
    setSelections: (selectionItems: SelectionFieldItem[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        await Promise.all(selectionItems.map(async (s) => {
          const { defaultGroupByFields } = selectGroupByFieldState(instanceKey)(getState());
          if (!defaultGroupByFields.some(d => d.value === s?.field?.value)) {
            dispatch(new actions.UpdateGroupByDefaultFields(instanceKey, [...defaultGroupByFields, s.field]));
          }
          await dispatch(actionCreators(api, instanceKey).loadGroupBySelectionTypes(s?.field?.value));
        }));
      }),
    addSelection: (selectedFieldItem: SelectionFieldItem): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        await dispatch(actionCreators(api, instanceKey).loadGroupBySelectionTypes(selectedFieldItem?.field?.value));

        const {
          defaultGroupByFields,
          selectionTypeInformation
        } = selectGroupByFieldState(instanceKey)(getState());

        const selectedGroupByFields = selectSelectionFieldItemsForCurrentReport(instanceKey)(getState());

        const selectionTypeInfo = selectionTypeInformation?.[selectedFieldItem?.field?.value];
        const selectionTypesForField = selectionTypeInfo?.selectionTypes;

        const fieldToAdd: SelectionFieldItem = {
          ...selectedFieldItem,
          field: {
            ...selectedFieldItem.field,
            showMissingValues: selectionTypeInfo.showMissingValues
          },
          ...(!selectedFieldItem.value && selectionTypesForField.length > 0 && {
            value: setDefaultValue(selectionTypesForField)
          }),
          selectionTypes: selectionTypesForField
        };

        if (!defaultGroupByFields.some(d => d.value === selectedFieldItem?.field?.value)) {
          dispatch(new actions.UpdateGroupByDefaultFields(instanceKey, [...defaultGroupByFields, selectedFieldItem.field]));
        }

        dispatch(setWorkingDefinitionSelections(
          [...selectedGroupByFields, fieldToAdd].map((e, i) => mapToSelectionDefinition(e, i + 1)),
          selectedGroupByFields?.[0]?.value?.rollingAverage
        ));
      }),
    loadGroupBySelectionTypes: (fieldId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        const {
          selectionTypeInformation
        } = selectGroupByFieldState(instanceKey)(getState());

        if (selectionTypeInformation[fieldId]) {
          return;
        }

        const texts = getState().selectionReport.texts;
        
        dispatch(new actions.RequestGroupBySelectionTypes(instanceKey, fieldId));

        const result = (await api.loadGroupBySelectionTypes(fieldId)).data;
        const translationDescription = createDescriptionSelector(texts);
        const textLevel = translationDescription(selectionReportTranslations.group_by_level);
        const textAdjustToFilters = translationDescription(selectionReportTranslations.group_by_adjust_to_filters);

        const selectionTypes = result.selectionTypes.map(selectionType =>
          mapSelectionType(selectionType, textLevel, { value: 0, description: textAdjustToFilters })
        );
        
        dispatch(new actions.ResponseGroupBySelectionTypes(
          instanceKey, 
          fieldId, 
          selectionTypes, 
          result?.showMissingValues
        ));
      }),

    modifyGroupBySelections: (fields: SelectionFieldItem[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionSelections(
          fields.map((e, i) => mapToSelectionDefinition(e, i + 1)),
          fields?.[0]?.value?.rollingAverage
        ));
      })
  };
};
